import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { OfficeAiDock } from '@workchat/office';
import { Sparkles, X } from '../../../icons';
import { tWorkspace } from '../../../lib/workspace-i18n';
import { WorkspaceDocAiChat, type WorkspaceDocAiTarget } from './WorkspaceDocAiChat';
import './WorkspaceDocAi.css';

/**
 * 문서 편집기의 **플로팅 AI 진입점**(FAB) — 2026-08-15, PR2.
 *
 * ## 왜 호스트가 그리는가
 *
 * 챗박스 자체는 편집기 번들 안에 이미 들어 있다(GenOffice 벤더링이 통째로 들여왔다).
 * 그런데 **여는 버튼**까지 편집기 안에 두면 세 가지가 어긋난다.
 *
 *   ① 편집기 번들이 다섯 벌이라 같은 버튼이 다섯 번 실리고 서로 갈라진다.
 *   ② 한글(hwp)·평문 편집기에는 `.go-root` 자체가 없어 **일곱 중 둘이 빠진다.**
 *   ③ office CSS 는 전부 `.go-root` 로 스코프돼 호스트 DOM(패널 헤더 위 등)에 닿지 못한다.
 *
 * 그래서 버튼은 여기서 그리고, 편집기에는 `OfficeEditorHandle.ai` 라는 얇은 여닫이만 둔다.
 *
 * ## 두 변종 (PR3 에서 완성)
 *
 * | | 카드 | 여닫이 |
 * |---|---|---|
 * | GenOffice 5종 | 편집기 번들 안의 상류 패널(리스킨) | `dock.setOpen()` → `.go-ai-open` |
 * | 한글·평문 | **호스트가 그리는** `WorkspaceDocAiChat` | 이 컴포넌트의 지역 상태 |
 *
 * 버튼은 일곱이 **문자 그대로 같다.** 예고한 대로 갈리는 것은 `dock` 유무뿐이고, 폴백
 * 쪽은 `target` 이 대신 온다. 둘 다 없으면(편집기가 아직 안 떴다) 아무것도 안 그린다.
 *
 * ## 위치
 *
 * `<DocPanel>` 의 **형제**로 둔다(자식이 아니다). `.doc-panel__body` 는 `overflow:auto` 라
 * 자식으로 넣으면 스크롤에 딸려 가고 헤더를 덮지 못한다. 형제로 두면
 * `.workspace-docpanel` 이 그대로 기준 상자가 된다 — 인라인일 때 `position:relative`,
 * 전체화면일 때 `position:fixed` 가 **이미 있어서** 두 상태에서 같은 코드가 옳다.
 * (`document.body` 포털 + `fixed` 로 두면 인라인 300px 모드에서 워크스페이스 목록을 덮는다.)
 */
export function WorkspaceDocAi({ dock, target, slot }: {
  /**
   * 편집기 내장 AI 카드의 여닫이(GenOffice 5종). `null` 이면 상류 카드가 없다 —
   * 그때 `target` 이 오면 호스트 폴백 카드를 대신 그린다.
   */
  dock: OfficeAiDock | null;
  /** 호스트 폴백 카드가 문서에 닿는 창구(한글·평문). 없으면 폴백을 그리지 않는다. */
  target?: WorkspaceDocAiTarget | null;
  /**
   * 폴백 카드를 넣을 상자 — **편집 슬롯**(`.workspace-docpanel-editor`)이다.
   *
   * ⚠ 패널(`.workspace-docpanel`)에 그리면 안 된다. 인라인(≈300px) 모드에서 카드가 거의
   *   전체 높이라 **문서 패널 헤더(파일명·저장 버튼)를 덮는다.** 슬롯을 기준으로 두면
   *   상류 카드(PR2)와 같은 상자·같은 변수라 두 변종이 정확히 같은 자리에 뜬다.
   */
  slot?: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);
  /**
   * 상류 패널이 DOM 에 붙었는가.
   *
   * ⚠ markdown 은 **문서 로드가 끝난 뒤에야** 패널을 마운트한다(`markdown/App.tsx`).
   *   준비 전에 열면 빈 카드가 뜨므로 그때까지는 버튼을 비활성으로 둔다.
   *   `isReady()` 는 DOM 조회라 값이 바뀌어도 알림이 없어 — 준비될 때까지만 짧게 폴링한다.
   */
  const [ready, setReady] = useState(false);

  /** 폴백 변종인가 — 상류 카드가 없고 우리가 그릴 대상이 온 경우(한글·평문). */
  const fallback = !dock && !!target;

  // 파일 전환·편집기 재마운트로 카드의 주인이 갈리면 상태를 처음으로 되돌린다.
  useEffect(() => {
    setOpen(false);
    // 폴백 카드는 우리가 그리므로 기다릴 것이 없다 — 상류 패널의 마운트 시점 문제가 없다.
    setReady(dock ? dock.isReady() : !!target);
  }, [dock, target]);

  useEffect(() => {
    if (!dock || ready) return undefined;
    const id = window.setInterval(() => {
      if (dock.isReady()) setReady(true);
    }, 300);
    return () => window.clearInterval(id);
  }, [dock, ready]);

  // 편집을 닫거나 패널이 사라질 때 카드도 함께 닫는다 — 다시 열었을 때 카드가 남아 있으면
  // "닫았는데 왜 열려 있나" 가 된다(편집기는 언마운트되지 않고 감춰지기만 한다).
  useEffect(() => () => { dock?.setOpen(false); }, [dock]);

  const toggle = useCallback((next: boolean) => {
    // 상류 카드는 편집기가 상태를 쥐고(클래스), 폴백 카드는 이 컴포넌트가 쥔다.
    if (dock) dock.setOpen(next);
    else if (!target) return;
    setOpen(next);
  }, [dock, target]);

  /**
   * Esc 로 카드만 닫는다.
   *
   * ⚠ `DocPanel` 도 document 에서 Esc 를 듣고 **패널 전체를 닫는다**. 그래서 반드시
   *   **캡처 단계**에 등록하고 전파를 끊어야 한다 — 안 하면 카드와 문서 패널이 함께 닫힌다
   *   (이 화면에서 가장 깨지기 쉬운 회귀라 테스트로 고정해 뒀다).
   */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      toggle(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, toggle]);

  if (!dock && !fallback) return null;

  const label = tWorkspace(open ? 'workspace.ai.close' : 'workspace.ai.open');

  return (
    <>
      <button
        type="button"
        className={`wsai-fab${open ? ' wsai-fab--open' : ''}`}
        onClick={() => toggle(!open)}
        disabled={!ready}
        aria-expanded={open}
        aria-label={label}
        title={ready ? label : tWorkspace('workspace.ai.preparing')}
      >
        {open ? <X size={20} aria-hidden /> : <Sparkles size={20} aria-hidden />}
      </button>
      {/*
        폴백 카드는 **편집 슬롯 안에** 그린다 — 상류 카드(PR2)와 같은 상자여야 두 변종이
        같은 자리에 뜬다. 포털을 쓰는 이유는 FAB(패널의 자식)와 카드(슬롯의 자식)가 DOM 상
        다른 곳에 있어야 하는데 여닫이 상태는 하나이기 때문이다.

        ⚠ 슬롯에는 한글 편집기가 **명령형으로** 붙인 DOM 이 함께 산다. 카드는 절대 위치라
          흐름을 건드리지 않고, 한글 편집기의 정리(`scope.remove()`)도 자기 노드만 지운다 —
          두 소유권이 겹치지 않는다.
      */}
      {fallback && target && slot && createPortal(
        <WorkspaceDocAiChat open={open} onClose={() => toggle(false)} target={target} />,
        slot,
      )}
    </>
  );
}
